import { useState, useMemo } from 'react';
import { Lock, Unlock } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

const PRESETS = [
  { label: '16:9', w: 16, h: 9 },
  { label: '9:16', w: 9, h: 16 },
  { label: '4:3', w: 4, h: 3 },
  { label: '3:2', w: 3, h: 2 },
  { label: '1:1', w: 1, h: 1 },
  { label: '21:9', w: 21, h: 9 },
  { label: '4:5', w: 4, h: 5 },
  { label: '2.35:1', w: 47, h: 20 },
];

function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : a;
}

export function AspectRatio() {
  const [w, setW] = useState(1920);
  const [h, setH] = useState(1080);
  const [ratioW, setRatioW] = useState(16);
  const [ratioH, setRatioH] = useState(9);
  const [locked, setLocked] = useState(true);

  const resolved = useMemo(() => {
    const g = gcd(w, h);
    return { rw: w / g, rh: h / g };
  }, [w, h]);

  function applyPreset(rw: number, rh: number) {
    setRatioW(rw);
    setRatioH(rh);
    setH(Math.round((w * rh) / rw));
  }

  function setNewW(nw: number) {
    setW(nw);
    if (locked) setH(Math.round((nw * ratioH) / ratioW));
  }

  function setNewH(nh: number) {
    setH(nh);
    if (locked) setW(Math.round((nh * ratioW) / ratioH));
  }

  return (
    <div>
      <ToolHeader
        toolId="aspect-ratio"
        title="Aspect ratio calculator"
        description="Resize maintaining aspect ratio. Or input dimensions to find the simplified ratio."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ratio W">
              <Input
                type="number"
                value={ratioW}
                onChange={(e) => {
                  const v = Number(e.target.value) || 1;
                  setRatioW(v);
                  if (locked) setH(Math.round((w * ratioH) / v));
                }}
              />
            </Field>
            <Field label="Ratio H">
              <Input
                type="number"
                value={ratioH}
                onChange={(e) => {
                  const v = Number(e.target.value) || 1;
                  setRatioH(v);
                  if (locked) setH(Math.round((w * v) / ratioW));
                }}
              />
            </Field>
          </div>

          <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
            <Field label="Width">
              <Input
                type="number"
                value={w}
                onChange={(e) => setNewW(Number(e.target.value) || 0)}
              />
            </Field>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setLocked((l) => !l)}
              className="self-end mb-1.5"
            >
              {locked ? <Lock size={14} /> : <Unlock size={14} />}
            </Button>
            <Field label="Height">
              <Input
                type="number"
                value={h}
                onChange={(e) => setNewH(Number(e.target.value) || 0)}
              />
            </Field>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => applyPreset(p.w, p.h)}
                className="px-2.5 py-1 text-xs rounded-md border border-border hover:border-accent hover:bg-accent/5 transition"
              >
                {p.label}
              </button>
            ))}
          </div>
        </Card>

        <Card className="flex flex-col items-center justify-center">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle">Simplified ratio</div>
          <div className="text-4xl font-semibold mt-2 font-mono tabular-nums">
            {resolved.rw}:{resolved.rh}
          </div>
          <div className="mt-6 w-full">
            <div className="text-2xs text-fg-subtle mb-2">Visual preview</div>
            <div className="bg-bg-inset rounded-md p-3 flex items-center justify-center min-h-[120px]">
              <div
                className="bg-accent/30 border border-accent rounded"
                style={{
                  width: '100%',
                  maxWidth: '180px',
                  aspectRatio: `${ratioW} / ${ratioH}`,
                }}
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default AspectRatio;
